/*
 * Copyright 2026 Sony Corporation
 */

import type { Device, DeviceStateResponse } from '@ips-device-verse/twin-service/schema';
import { useCallback, useEffect, useRef, useState } from 'react';

import { useAuth } from '../contexts/auth-context';
import type { ResolvedOptics } from '../scene/camera-optics';
import type { RigManager, RigDefinition } from '../scene/rig-manager';
import type { Transform } from '../scene/types';

export interface UseCameraRigsOptions {
  worldId: string;
  devices: readonly Device[];
  selectedId: string | undefined;
  /** Shadows pushed over the socket, keyed by device. */
  live?: Record<string, DeviceStateResponse>;
  /** Devices the assistant has proposed; drawn, but not written to anything. */
  preview?: readonly RigDefinition[];
  /** What each frustum is drawn with, keyed by device. */
  optics?: Record<string, ResolvedOptics>;
}

export interface UseCameraRigsResult {
  attach: (rigs: RigManager) => void;
  onTransformChanged: (deviceId: string, transform: Transform) => void;
  /** Where each device stands, following a drag in progress. */
  poses: Record<string, Transform>;
  /** Set when a drag could not be written to the twin. */
  writeError: Error | undefined;
}

/** Floating point survives a round trip through JSON, but not exactly. */
const EPSILON = 1e-4;

const sameVector = (a: Transform['position'], b: Transform['position']) =>
  Math.abs(a.x - b.x) < EPSILON && Math.abs(a.y - b.y) < EPSILON && Math.abs(a.z - b.z) < EPSILON;

const sameTransform = (a: Transform, b: Transform) =>
  sameVector(a.position, b.position) && sameVector(a.rotation, b.rotation);

/**
 * Places the rigs and writes a drag back to the twin.
 *
 * Placement comes from the shadow, not from the device row: `reported` is the
 * device's own answer and beats anything the cloud believes, and `desired` is
 * what the user last asked for. A device whose gateway has never run has
 * neither, and the rig is spawned from the scene bounds instead.
 */
export const useCameraRigs = ({
  worldId,
  devices,
  selectedId,
  live,
  preview,
  optics,
}: UseCameraRigsOptions): UseCameraRigsResult => {
  const { api } = useAuth();
  const rigsRef = useRef<RigManager>(null);
  const [transforms, setTransforms] = useState<Record<string, Transform>>({});
  const [dragged, setDragged] = useState<Record<string, Transform>>({});
  const [writeError, setWriteError] = useState<Error>();

  const deviceIds = devices.map((device) => device.device_id).join(',');

  useEffect(() => {
    let current = true;

    Promise.all(
      devices.map(async (device) => {
        // A device with no shadow yet is normal, not an error.
        const state = await api.getDeviceState(worldId, device.device_id).catch(() => undefined);
        const transform = state?.state.reported?.transform ?? state?.state.desired?.transform ?? device.transform;
        return [device.device_id, transform] as const;
      })
    ).then((entries) => {
      if (!current) {
        return;
      }
      setTransforms(
        Object.fromEntries(entries.filter((entry): entry is [string, Transform] => entry[1] !== undefined))
      );
    });

    return () => {
      current = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, worldId, deviceIds]);

  /**
   * Where each device stands.
   *
   * A transform this tab dragged is held until the device reports that same
   * transform back. Releasing it when the HTTP write returns is far too early:
   * the 202 arrives long before the gateway has applied anything, and the next
   * echo is usually a mid-drag or heartbeat value that drags the rig backwards.
   */
  const poseOf = (deviceId: string): Transform | undefined => {
    const confirmed = live?.[deviceId]?.state.reported?.transform;
    const local = dragged[deviceId];

    if (local && !(confirmed && sameTransform(confirmed, local))) {
      return local;
    }
    return confirmed ?? live?.[deviceId]?.state.desired?.transform ?? transforms[deviceId];
  };

  const poses = Object.fromEntries(
    devices.map((device) => [device.device_id, poseOf(device.device_id)]).filter(([, pose]) => pose !== undefined)
  ) as Record<string, Transform>;

  const definitions: RigDefinition[] = [
    ...devices.map((device) => ({
      deviceId: device.device_id,
      transform: poses[device.device_id],
      optics: optics?.[device.device_id],
    })),
    ...(preview ?? []),
  ];
  const definitionsKey = JSON.stringify(definitions);

  useEffect(() => {
    rigsRef.current?.sync(JSON.parse(definitionsKey) as RigDefinition[]);
  }, [definitionsKey]);

  useEffect(() => {
    rigsRef.current?.select(selectedId);
  }, [selectedId]);

  const attach = useCallback(
    (rigs: RigManager) => {
      rigsRef.current = rigs;
      rigs.sync(JSON.parse(definitionsKey) as RigDefinition[]);
      rigs.select(selectedId);
    },
    [definitionsKey, selectedId]
  );

  const onTransformChanged = useCallback(
    (deviceId: string, transform: Transform) => {
      // Kept apart from the loaded transforms: feeding a drag back into the rig
      // definitions would have the panel fighting the gizmo for the same value.
      setDragged((previous) => ({ ...previous, [deviceId]: transform }));

      // The rig has already moved; this is the write to the twin catching up.
      api.writeDeviceState(worldId, deviceId, { transform }).then(
        () => setWriteError(undefined),
        (error: Error) => setWriteError(error)
      );
    },
    [api, worldId]
  );

  return { attach, onTransformChanged, poses, writeError };
};
